import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import {
  Menu,
  Search,
  Plus,
  Heart,
  MessageCircle,
  Star,
  Share2,
  ChevronRight,
  ChevronDown,
  Layers,
  ArrowUp,
  ArrowDown,
  Film,
  Clapperboard,
  Plane,
  Coffee,
  Utensils,
  Music2,
  Smile,
  Gamepad2,
  BookOpen,
  Cpu,
  Dumbbell,
  PawPrint,
  Grid2X2,
  X,
  SlidersHorizontal,
  Play,
  Bell,
  UserRoundPlus,
  Settings,
  Bookmark,
  History,
  Send,
  Trash2,
  LockKeyhole,
  Volume2,
  Info,
  HelpCircle,
  ShieldCheck,
  MapPin,
  AtSign,
  Hash,
  ImagePlus,
  Check,
  LogOut,
  ArrowLeft,
  Compass,
  Copy,
  ExternalLink,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  videos as seedVideos,
  categories as demoCategories,
  episodes,
  seedComments,
  filterVideos,
  formatCount,
  type VideoItem,
  type EpisodeItem,
} from "./data";
import { readState, STORAGE_KEY, toggleId, type SavedState } from "./storage";
import { useNavigation, type Page } from "./navigation";
import { bindNativeLifecycle, shareVideo } from "./platform";
import { Empty, Header, IconButton, Modal, VideoPlayer } from "./components";
import CameraPage from "./Camera";
import { useCatalog } from "./useCatalog";
import { IS_DEMO, filterNavigationVideos, type NavigationItem } from "./api";
import { useMember, AuthDialog } from "./auth";
import {
  TemplateProvider,
  TemplateRuntimeProvider,
  TemplatePage,
  TemplateBlock,
  TemplateFeedBlocks,
  FeedTopNavigation,
  homeTopNavigation,
  useTemplateConfig,
} from "./TemplateRenderer";
import type { CSSProperties } from "react";

const categoryIcons: LucideIcon[] = [
  Film,
  Clapperboard,
  Plane,
  Coffee,
  Utensils,
  Music2,
  Smile,
  Gamepad2,
  BookOpen,
  Cpu,
  Dumbbell,
  PawPrint,
];
const hotSearches = [
  "城市故事",
  "人生旅行日记",
  "慢下来，听见生活",
  "电影里的温柔瞬间",
  "一个人的旅行",
  "灯火依旧",
];
const conversations = [
  {
    name: "夏天的海",
    text: "好喜欢你的旅行记录！",
    time: "10:24",
    image: "/media/lake.jpg",
    unread: true,
  },
  {
    name: "阿杰同学",
    text: "下次一起去拍照吧～",
    time: "昨天",
    image: "/media/city.jpg",
  },
  {
    name: "小鹿 Lulu",
    text: "已赞了你的作品",
    time: "昨天",
    image: "/media/night-portrait.jpg",
  },
  {
    name: "摄影师阿凯",
    text: "这个机位太好了，求教程！",
    time: "周一",
    image: "/media/city.jpg",
  },
  {
    name: "城市漫游",
    text: "周末有拍摄计划吗？",
    time: "周一",
    image: "/media/lake.jpg",
  },
];
const tabs: { id: Page; label: string }[] = [
  { id: "home", label: "首页" },
  { id: "discover", label: "发现" },
  { id: "create", label: "发布" },
  { id: "messages", label: "消息" },
  { id: "profile", label: "我" },
];

export default function App() {
  const {
    template,
    navigation: templateNavigation,
    error: templateError,
    preview: templatePreview,
    ready: templateReady,
  } = useTemplateConfig();
  const { nav, navigate, replace, back } = useNavigation(
    template.pages,
    templateReady,
  );
  const pageConfig = (id: string) =>
    template.pages.find((page) => page.id === id)!;

  const [store, setStore] = useState(readState);
  const member = useMember();
  const [profileSaving, setProfileSaving] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  useEffect(() => {
    if (member.user)
      setStore((s) => ({
        ...s,
        profile: { name: member.user!.nickname, bio: member.user!.bio || "" },
      }));
  }, [member.user]);
  const {
    items,
    setItems,
    loading,
    moreLoading,
    error: catalogError,
    detailError,
    detailLoading,
    total,
    menu,
    customNavigation: fallbackNavigation,
    navigationError: fallbackNavigationError,
    navigationLoading: fallbackNavigationLoading,
    retryNavigation,
    hasMore,
    loadMore,
    retry,
    loadDetail,
  } = useCatalog();
  const customNavigation = templateNavigation || fallbackNavigation;
  const navigationError = templateNavigation ? "" : fallbackNavigationError;
  const navigationLoading = templateNavigation
    ? false
    : fallbackNavigationLoading;
  const categories = IS_DEMO
    ? demoCategories
    : [
        ...new Set([
          ...menu.types,
          ...items.filter((v) => v.remote).map((v) => v.category),
          ...menu.labels,
        ]),
      ];
  const suggestedSearches = IS_DEMO
    ? hotSearches
    : items.slice(0, 6).map((v) => v.title);
  const sharedId = new URLSearchParams(location.search).get("video");
  const [forceVideoFeed, setForceVideoFeed] = useState(!!sharedId);
  const [currentId, setCurrentId] = useState(
    sharedId || (IS_DEMO ? seedVideos[0].id : ""),
  );
  const [category, setCategory] = useState("全部推荐");
  const [externalNavigation, setExternalNavigation] =
    useState<NavigationItem | null>(null);
  const [selectedNavigationId, setSelectedNavigationId] = useState("");
  const selectedNavigation =
    customNavigation?.items.find((item) => item.id === selectedNavigationId) ||
    (externalNavigation?.id === selectedNavigationId
      ? externalNavigation
      : undefined);
  const drawerItems: NavigationItem[] = IS_DEMO
    ? demoCategories.map((name) => ({
        id: name,
        name,
        icon: "",
        targetType: "categories",
        categories: [{ id: name, name }],
        keywords: [],
        externalUrl: "",
      }))
    : customNavigation?.items || [];
  const [channel, setChannel] = useState("推荐");
  const [categoryQuery, setCategoryQuery] = useState("");
  const [sort, setSort] = useState("推荐");
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [commentText, setCommentText] = useState("");
  const [commentReply, setCommentReply] = useState("");
  const [episodeNumber, setEpisodeNumber] = useState(
    seedVideos.find((v) => v.id === sharedId)?.episode || 12,
  );
  const [reverseEpisodes, setReverseEpisodes] = useState(false);
  const [episodeTab, setEpisodeTab] = useState("选集");
  const [profileTab, setProfileTab] = useState("作品");
  const [libraryTab, setLibraryTab] = useState("合集");
  const [discoverCategory, setDiscoverCategory] = useState("全部推荐");
  const [toast, setToast] = useState("");
  const toastTimer = useRef<number>(0);
  const swipeStart = useRef({ x: 0, y: 0 });
  const swiped = useRef(false);
  const [upload, setUpload] = useState<{ file: File; url: string } | null>(
    null,
  );
  const objectUrls = useRef<string[]>([]);
  const [caption, setCaption] = useState(store.draft);
  const [publishCategory, setPublishCategory] = useState("生活");
  const [visibility, setVisibility] = useState("公开");
  const [allowComments, setAllowComments] = useState(true);
  const [publishCollection, setPublishCollection] = useState("不加入合集");
  const [editName, setEditName] = useState(store.profile.name);
  const [editBio, setEditBio] = useState(store.profile.bio);
  const [chatName, setChatName] = useState("夏天的海");
  const [chatText, setChatText] = useState("");
  const [chats, setChats] = useState<Record<string, string[]>>({});
  const [noticeTitle, setNoticeTitle] = useState("系统通知");
  const [noticeText, setNoticeText] = useState(
    "欢迎来到樱桃视频。这是本地前端演示，消息服务将在接入后台后启用。",
  );
  const [readMessages, setReadMessages] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const video = items.find((v) => v.id === currentId) ||
    items[0] || {
      id: "pending",
      title: "",
      description: "",
      creator: "",
      category: "",
      cover: "/icon.svg",
      likes: 0,
      comments: 0,
      saves: 0,
      collection: "",
      episode: 1,
      duration: "",
    };
  let feed = selectedNavigation
    ? filterNavigationVideos(
        items.filter((v) => !v.private),
        selectedNavigation,
      )
    : filterVideos(
        items.filter((v) => !v.private),
        category,
      );
  if (channel === "关注")
    feed = feed.filter((v) => store.followed.includes(v.creator));
  if (channel === "精选" && IS_DEMO)
    feed = feed.filter((v) => v.likes >= 10000);
  if (sort === "热门") feed = [...feed].sort((a, b) => b.likes - a.likes);
  if (sort === "最新") feed = [...feed].reverse();
  const current = feed.find((v) => v.id === currentId) || feed[0];
  useEffect(() => {
    // Do not report an empty custom channel just because its first match is on
    // a later feed page. Once matches exist, normal feed pagination takes over.
    if (
      selectedNavigation &&
      !feed.length &&
      hasMore &&
      !loading &&
      !moreLoading &&
      !catalogError
    )
      loadMore();
  }, [
    selectedNavigation,
    feed.length,
    hasMore,
    loading,
    moreLoading,
    catalogError,
    loadMore,
  ]);
  const displayVideo = current || video;
  const baseComments =
    displayVideo.remote || displayVideo.id.startsWith("local-")
      ? []
      : seedComments;
  const comments = store.comments[displayVideo.id] || baseComments;
  const isFeed = nav.page === "home";
  const catalogStatusNeeded =
    pageConfig(nav.page)?.kind !== "custom" ||
    pageConfig(nav.page)?.blocks.some(
      (block) =>
        block.enabled &&
        template.components.some(
          (component) =>
            component.id === block.componentId &&
            component.type === "video-grid",
        ),
    );
  const collectionEpisodes: EpisodeItem[] = displayVideo.remote
    ? displayVideo.episodes || []
    : displayVideo.collection === "城市故事"
      ? episodes
      : items
          .filter((v) => v.collection === displayVideo.collection)
          .map((v) => ({
            number: v.episode,
            title: v.title,
            duration: v.duration,
            cover: v.cover,
          }));
  useEffect(() => {
    if (displayVideo.remote) void loadDetail(displayVideo.id);
  }, [displayVideo.id, displayVideo.remote, loadDetail]);
  const hasNav =
    !["create", "publish"].includes(nav.page) &&
    !!template.bottomMenu?.items.length &&
    (["home", "discover", "messages", "profile"].includes(nav.page) ||
      pageConfig(nav.page)?.kind === "custom" ||
      !!template.bottomMenu?.items.some(
        (i) => i.url === pageConfig(nav.page)?.url,
      ));
  const notify = useCallback((text: string) => {
    clearTimeout(toastTimer.current);
    setToast(text);
    toastTimer.current = window.setTimeout(() => setToast(""), 3200);
  }, []);
  const update = (updater: (s: SavedState) => SavedState) => setStore(updater);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch {
      notify("本机存储空间不足，部分操作无法保存");
    }
  }, [store, notify]);
  useEffect(() => {
    let dispose: (() => void) | undefined,
      cancelled = false;
    void bindNativeLifecycle(() => history.back()).then((fn) => {
      if (cancelled) fn();
      else dispose = fn;
    });
    return () => {
      cancelled = true;
      dispose?.();
    };
  }, []);
  useEffect(
    () => () => {
      objectUrls.current.forEach((url) => URL.revokeObjectURL(url));
      clearTimeout(toastTimer.current);
    },
    [],
  );
  useEffect(() => {
    setItems((s) =>
      s.map((v) => (v.own ? { ...v, creator: store.profile.name } : v)),
    );
  }, [store.profile.name]);
  useEffect(() => {
    if (!current || nav.page !== "home") return;
    setStore((s) =>
      s.history[0] === current.id
        ? s
        : {
            ...s,
            history: [
              current.id,
              ...s.history.filter((id) => id !== current.id),
            ].slice(0, 100),
          },
    );
  }, [current?.id, nav.page]);
  useEffect(() => {
    if (nav.page === "messages") setReadMessages(true);
  }, [nav.page]);
  function chooseCategory(name: string) {
    setSelectedNavigationId("");
    setCategory(name);
    setChannel("推荐");
    setSort("推荐");
    setCategoryQuery("");
    const first = filterVideos(
      items.filter((v) => !v.private),
      name,
    )[0];
    if (first) {
      setCurrentId(first.id);
      setEpisodeNumber(first.episode);
    }
    if (nav.page === "home" && nav.overlay) back();
    else navigate("home");
  }
  function chooseNavigation(item: NavigationItem) {
    if (IS_DEMO) return chooseCategory(item.name);
    if (item.targetType === "external_url") {
      try {
        const url = new URL(item.externalUrl);
        if (!["http:", "https:"].includes(url.protocol)) throw new Error();
        location.assign(url.href);
      } catch {
        notify("菜单链接配置不正确");
      }
      return;
    }
    setSelectedNavigationId(item.id);
    setCategory(item.name);
    setChannel("推荐");
    setSort("推荐");
    setCategoryQuery("");
    const first = filterNavigationVideos(
      items.filter((v) => !v.private),
      item,
    )[0];
    if (first) {
      setCurrentId(first.id);
      setEpisodeNumber(first.episode);
    }
    if (nav.overlay) back();
    else navTo("home");
  }
  function nextVideo(direction = 1) {
    if (
      direction > 0 &&
      hasMore &&
      feed.findIndex((v) => v.id === displayVideo.id) >= feed.length - 3
    )
      loadMore();
    if (feed.length < 2) {
      notify("这个频道已经看完啦");
      return;
    }
    const index = Math.max(
      0,
      feed.findIndex((v) => v.id === displayVideo.id),
    );
    const next = feed[(index + direction + feed.length) % feed.length];
    setCurrentId(next.id);
    setEpisodeNumber(next.episode);
  }
  function openVideo(item: VideoItem) {
    setForceVideoFeed(true);
    setSelectedNavigationId("");
    setCategory("全部推荐");
    setChannel("推荐");
    setCurrentId(item.id);
    setEpisodeNumber(item.episode);
    navigate("home");
  }
  function showInfo(title: string, text: string) {
    setNoticeTitle(title);
    setNoticeText(text);
    navigate(nav.page, "notice");
  }
  function openSearch(term = "") {
    setQuery(term);
    setSearchTerm(term);
    navigate("search");
  }
  function search(term: string) {
    if (!term.trim()) return;
    setQuery(term);
    setSearchTerm(term.trim());
    update((s) => ({
      ...s,
      searches: [
        term.trim(),
        ...s.searches.filter((t) => t !== term.trim()),
      ].slice(0, 8),
    }));
  }
  function addComment(e: FormEvent) {
    e.preventDefault();
    if (!commentText.trim()) return;
    update((s) => ({
      ...s,
      comments: {
        ...s.comments,
        [displayVideo.id]: [
          {
            id: crypto.randomUUID(),
            user: s.profile.name,
            text: `${commentReply ? `回复 @${commentReply}：` : ""}${commentText.trim()}`,
            time: "刚刚",
            likes: 0,
          },
          ...(s.comments[displayVideo.id] || baseComments),
        ],
      },
    }));
    setCommentText("");
    setCommentReply("");
    notify("评论已保存到本机");
  }
  function selectFile(file: File) {
    if (!file.type.startsWith("video/")) {
      notify("请选择视频文件");
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      notify("演示版支持 100 MB 以内的视频");
      return;
    }
    const url = URL.createObjectURL(file);
    objectUrls.current.push(url);
    setUpload({ file, url });
    navigate("publish");
  }
  function publish() {
    if (!upload) {
      notify("请先选择或拍摄视频");
      return;
    }
    if (!caption.trim()) {
      notify("给作品写一句描述吧");
      return;
    }
    const id = `local-${Date.now()}`;
    const isPrivate = visibility === "仅自己可见";
    setItems((s) => [
      {
        id,
        title: caption.trim().slice(0, 20),
        description: caption,
        creator: store.profile.name,
        category: publishCategory,
        src: upload.url,
        cover: "/media/night-portrait.jpg",
        likes: 0,
        comments: 0,
        saves: 0,
        collection:
          publishCollection === "不加入合集" ? "我的作品" : publishCollection,
        episode: 1,
        duration: "本地",
        own: true,
        private: isPrivate,
        allowComments,
      },
      ...s,
    ]);
    setCurrentId(id);
    setCategory("全部推荐");
    setChannel("推荐");
    update((s) => ({ ...s, draft: "" }));
    setCaption("");
    setUpload(null);
    if (isPrivate) {
      setProfileTab("私密");
      replace("profile");
    } else replace("home");
    notify("作品已加入本地演示，刷新后视频不会保留");
  }
  function toggle(
    field: "liked" | "saved" | "followed" | "collections",
    id: string,
  ) {
    update((s) => ({ ...s, [field]: toggleId(s[field], id) }));
  }
  function navTo(page: Page) {
    if (page === "home") setForceVideoFeed(false);
    if (page === "create") {
      setCaption(store.draft);
      setAllowComments(store.settings.comments);
    }
    navigate(page);
  }
  const navFooter = (
    <nav className="bottom-nav" aria-label="主导航">
      {template.bottomMenu?.items
        .filter((i) => i.status === "启用")
        .map((item) => {
          const target = template.pages.find(
            (p) => p.url === item.url && p.enabled,
          );
          if (!target) return null;
          const plus = item.icon === "plus";
          return (
            <button
              type="button"
              key={item.id}
              className={`${nav.page === target.id ? "active" : ""} ${plus ? "create-button" : ""}`}
              onClick={() => navTo(target.id)}
              aria-label={item.name}
              aria-current={nav.page === target.id ? "page" : undefined}
            >
              {plus ? (
                <span>
                  <Plus size={26} strokeWidth={2.8} />
                </span>
              ) : (
                <span>{item.name}</span>
              )}
              {target.id === "messages" && IS_DEMO && !readMessages && (
                <i className="nav-dot" />
              )}
            </button>
          );
        })}
    </nav>
  );
  function cardGrid(list: VideoItem[]) {
    return (
      <div className="video-grid">
        {list.map((item) => (
          <button
            className="video-card"
            key={item.id}
            onClick={() => openVideo(item)}
          >
            <div className="card-image">
              <img src={item.cover} alt="" loading="lazy" />
              <span>
                <Play size={12} fill="currentColor" />{" "}
                {item.remote ? item.duration : formatCount(item.likes)}
              </span>
              {item.src && <b className="playable-tag">可播放</b>}
            </div>
            <h3>{item.title}</h3>
            <p>
              @{item.creator}
              <span>{item.duration}</span>
            </p>
          </button>
        ))}
      </div>
    );
  }

  return (
    <TemplateProvider value={template}>
      <TemplateRuntimeProvider
        value={{
          goToUrl: (url) => {
            if (url.startsWith("#")) {
              const target = template.pages.find(
                (p) => (p.url === url || p.id === url.slice(1)) && p.enabled,
              );
              if (target) navTo(target.id);
              else notify("关联页面不可用");
            } else {
              try {
                const target = new URL(url);
                if (["https:", "http:"].includes(target.protocol))
                  location.assign(target.href);
              } catch {
                notify("链接不可用");
              }
            }
          },
          videoGrid: () => cardGrid(items.filter((v) => !v.private)),
          openVideo,
          selectedNavigationId,
          openNavigation: (item) => {
            setExternalNavigation(item);
            chooseNavigation(item);
          },
        }}
      >
        <div
          className="workspace"
          data-template={template.code}
          data-template-preview={templatePreview || undefined}
          data-template-fallback={templateError ? true : undefined}
          style={
            {
              "--accent": template.theme.accent,
              "--surface": template.theme.surface,
            } as CSSProperties
          }
        >
          <aside className="desktop-intro">
            <div className="wordmark">
              <span className="brand-icon">
                <Play size={21} fill="currentColor" />
              </span>
              樱桃视频<span className="wordmark-en">CHERRY</span>
            </div>
            <span className="eyebrow">EVERY CHERRY, A STORY</span>
            <h1>
              在每一帧里，
              <br />
              遇见<span>新的世界。</span>
            </h1>
            <p>
              让好故事被看见，让平凡的日常
              <br />
              也有值得记住的瞬间。
            </p>
            <div className="desktop-links">
              {[
                {
                  icon: Film,
                  text: "沉浸观看",
                  sub: "故事开始的地方",
                  page: "home",
                },
                {
                  icon: Compass,
                  text: "发现精彩",
                  sub: "探索你的热爱",
                  page: "discover",
                },
                {
                  icon: Layers,
                  text: "我的合集",
                  sub: "收藏每一份心动",
                  page: "collection",
                },
              ]
                .filter(({ page }) => pageConfig(page)?.enabled)
                .map(({ icon: Icon, text, sub, page }) => (
                  <button key={page} onClick={() => navigate(page as Page)}>
                    <Icon size={21} />
                    <span>
                      {text}
                      <small>{sub}</small>
                    </span>
                    <ChevronRight size={16} />
                  </button>
                ))}
            </div>
            <div className="desktop-foot">
              <span className="online-dot" /> MOBILE EXPERIENCE · H5
            </div>
          </aside>
          <div className={`app-shell ${isFeed ? "feed-shell" : ""}`}>
            <div className="app-content" inert={nav.overlay ? true : undefined}>
              <main
                className={`main-view ${hasNav ? "with-nav" : ""} ${isFeed ? "feed-view" : ""}`}
              >
                {!IS_DEMO && catalogStatusNeeded && loading && (
                  <div className="catalog-state">
                    <span className="catalog-spinner" />
                    <h2>正在加载视频</h2>
                    <p>连接樱桃视频内容库</p>
                  </div>
                )}
                {!IS_DEMO && catalogStatusNeeded && catalogError && (
                  <div
                    className={
                      items.length ? "catalog-error-inline" : "catalog-state"
                    }
                  >
                    <p>{catalogError}</p>
                    <button
                      className="primary"
                      onClick={items.length ? loadMore : retry}
                    >
                      重新加载
                    </button>
                  </div>
                )}
                {pageConfig(nav.page)?.kind === "custom" && (
                  <TemplatePage
                    id={nav.page}
                    className="scroll-page custom-template-page"
                  >
                    <header className="simple-header">
                      <IconButton label="返回" onClick={back}>
                        <ArrowLeft size={24} />
                      </IconButton>
                      <h1>{pageConfig(nav.page).title}</h1>
                    </header>
                  </TemplatePage>
                )}
                {nav.page === "home" && (
                  <TemplatePage
                    id="home"
                    className="feed-page"
                    forceFeed={forceVideoFeed}
                  >
                    <TemplateBlock id="header">
                      <header className="feed-header">
                        <IconButton
                          label={
                            forceVideoFeed &&
                            pageConfig("home").layout === "video-grid"
                              ? "返回视频列表"
                              : "打开分类菜单"
                          }
                          onClick={() =>
                            forceVideoFeed &&
                            pageConfig("home").layout === "video-grid"
                              ? setForceVideoFeed(false)
                              : navigate("home", "categories")
                          }
                        >
                          {forceVideoFeed &&
                          pageConfig("home").layout === "video-grid" ? (
                            <ArrowLeft size={25} />
                          ) : (
                            <Menu size={25} />
                          )}
                        </IconButton>
                        {homeTopNavigation(template) ? (
                          <FeedTopNavigation />
                        ) : category === "全部推荐" ? (
                          <div className="feed-tabs">
                            {["关注", "精选", "推荐"].map((tab) => (
                              <button
                                className={channel === tab ? "selected" : ""}
                                key={tab}
                                onClick={() => setChannel(tab)}
                              >
                                {tab}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <button
                            className="category-title"
                            onClick={() => navigate("home", "categories")}
                          >
                            {category}
                            <ChevronDown size={16} />
                          </button>
                        )}
                        <IconButton label="搜索" onClick={() => openSearch()}>
                          <Search size={24} />
                        </IconButton>
                      </header>
                    </TemplateBlock>
                    <TemplateBlock id="sorting">
                      {!homeTopNavigation(template) &&
                        category !== "全部推荐" && (
                          <div className="feed-sort">
                            {["推荐", "最新", "热门"].map((t) => (
                              <button
                                className={sort === t ? "selected" : ""}
                                key={t}
                                onClick={() => setSort(t)}
                              >
                                {t}
                              </button>
                            ))}
                          </div>
                        )}
                    </TemplateBlock>
                    <TemplateBlock id="media">
                      {current ? (
                        <TemplateFeedBlocks>
                          <TemplateBlock id="media">
                            <div
                              className="feed-media"
                              onTouchStart={(e) => {
                                swipeStart.current = {
                                  x: e.touches[0].clientX,
                                  y: e.touches[0].clientY,
                                };
                                swiped.current = false;
                              }}
                              onTouchEnd={(e) => {
                                const dx =
                                    e.changedTouches[0].clientX -
                                    swipeStart.current.x,
                                  dy =
                                    e.changedTouches[0].clientY -
                                    swipeStart.current.y;
                                if (
                                  Math.abs(dy) > 65 &&
                                  Math.abs(dy) > Math.abs(dx)
                                ) {
                                  swiped.current = true;
                                  nextVideo(dy < 0 ? 1 : -1);
                                }
                              }}
                              onClickCapture={(e) => {
                                if (swiped.current) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  swiped.current = false;
                                }
                              }}
                              onDoubleClick={() => {
                                if (!store.liked.includes(displayVideo.id))
                                  toggle("liked", displayVideo.id);
                              }}
                            >
                              {displayVideo.remote && detailLoading && (
                                <div className="detail-status">
                                  正在读取分集…
                                </div>
                              )}
                              {displayVideo.remote && detailError && (
                                <button
                                  className="detail-status"
                                  onClick={() =>
                                    void loadDetail(displayVideo.id)
                                  }
                                >
                                  {detailError} · 点击重试
                                </button>
                              )}
                              <VideoPlayer
                                video={displayVideo}
                                active={!nav.overlay}
                                autoplay={store.settings.autoplay}
                                muted={store.settings.muted}
                                onMute={(muted) =>
                                  update((s) => ({
                                    ...s,
                                    settings: {
                                      ...s.settings,
                                      muted,
                                    },
                                  }))
                                }
                                onNext={() => {
                                  const index = collectionEpisodes.findIndex(
                                    (e) => e.number === episodeNumber,
                                  );
                                  if (
                                    displayVideo.remote &&
                                    index >= 0 &&
                                    index < collectionEpisodes.length - 1
                                  )
                                    setEpisodeNumber(
                                      collectionEpisodes[index + 1].number,
                                    );
                                  else nextVideo();
                                }}
                                onView={() => {}}
                              />
                            </div>
                          </TemplateBlock>
                          <TemplateBlock id="social">
                            <div className="social-rail">
                              <button
                                className={`creator-avatar ${store.followed.includes(displayVideo.creator) ? "following" : ""}`}
                                aria-label={
                                  store.followed.includes(displayVideo.creator)
                                    ? "取消关注作者"
                                    : "关注作者"
                                }
                                onClick={() => {
                                  toggle("followed", displayVideo.creator);
                                  notify(
                                    store.followed.includes(
                                      displayVideo.creator,
                                    )
                                      ? "已取消关注"
                                      : "已关注作者",
                                  );
                                }}
                              >
                                <img
                                  src={
                                    displayVideo.remote
                                      ? "/icon.svg"
                                      : "/media/night-portrait.jpg"
                                  }
                                  alt=""
                                />
                                <span>
                                  {store.followed.includes(
                                    displayVideo.creator,
                                  ) ? (
                                    <Check size={12} />
                                  ) : (
                                    <Plus size={12} />
                                  )}
                                </span>
                              </button>
                              <button
                                aria-label="点赞"
                                aria-pressed={store.liked.includes(
                                  displayVideo.id,
                                )}
                                className={
                                  store.liked.includes(displayVideo.id)
                                    ? "liked"
                                    : ""
                                }
                                onClick={() => toggle("liked", displayVideo.id)}
                              >
                                <Heart
                                  size={33}
                                  fill="currentColor"
                                  strokeWidth={1.5}
                                />
                                <span>
                                  {displayVideo.remote
                                    ? store.liked.includes(displayVideo.id)
                                      ? "已点赞"
                                      : "点赞"
                                    : formatCount(
                                        displayVideo.likes +
                                          Number(
                                            store.liked.includes(
                                              displayVideo.id,
                                            ),
                                          ),
                                      )}
                                </span>
                              </button>
                              <button
                                aria-label="查看评论"
                                onClick={() =>
                                  displayVideo.allowComments === false
                                    ? notify("作者已关闭评论")
                                    : navigate("home", "comments")
                                }
                              >
                                <MessageCircle
                                  size={32}
                                  fill="currentColor"
                                  strokeWidth={1.5}
                                />
                                <span>
                                  {displayVideo.comments +
                                    Math.max(
                                      0,
                                      comments.length - baseComments.length,
                                    )}
                                </span>
                              </button>
                              <button
                                aria-label="收藏视频"
                                aria-pressed={store.saved.includes(
                                  displayVideo.id,
                                )}
                                className={
                                  store.saved.includes(displayVideo.id)
                                    ? "saved"
                                    : ""
                                }
                                onClick={() => toggle("saved", displayVideo.id)}
                              >
                                <Star
                                  size={33}
                                  fill="currentColor"
                                  strokeWidth={1.5}
                                />
                                <span>
                                  {displayVideo.remote
                                    ? store.saved.includes(displayVideo.id)
                                      ? "已收藏"
                                      : "收藏"
                                    : formatCount(
                                        displayVideo.saves +
                                          Number(
                                            store.saved.includes(
                                              displayVideo.id,
                                            ),
                                          ),
                                      )}
                                </span>
                              </button>
                              <button
                                aria-label="分享视频"
                                onClick={() => navigate("home", "share")}
                              >
                                <Share2 size={29} fill="currentColor" />
                                <span>分享</span>
                              </button>
                              <div className="music-disc">
                                <Music2 size={19} />
                              </div>
                            </div>
                          </TemplateBlock>
                          <TemplateBlock id="description">
                            <div className="feed-copy">
                              <div className="content-label">
                                <Film size={12} /> {displayVideo.category}
                                <ChevronRight size={12} />
                              </div>
                              <h2 title={displayVideo.title}>
                                {displayVideo.title}
                              </h2>
                              <p>{displayVideo.description}</p>
                              <span
                                className="video-topic"
                                onClick={() =>
                                  chooseCategory(displayVideo.category)
                                }
                              >
                                #{displayVideo.collection}　#
                                {displayVideo.category}
                              </span>
                            </div>
                          </TemplateBlock>
                          <TemplateBlock id="collection">
                            <button
                              className="collection-strip"
                              onClick={() => navigate("home", "episodes")}
                            >
                              <Layers size={20} />
                              <strong>合集 · {displayVideo.collection}</strong>
                              <span>
                                {displayVideo.demo ? "播放示例" : "选集观看"}
                              </span>
                              <ChevronRight size={18} />
                            </button>
                          </TemplateBlock>
                          <TemplateBlock id="pagination">
                            <div className="feed-pagination">
                              <IconButton
                                label="上一个视频"
                                onClick={() => nextVideo(-1)}
                              >
                                <ArrowUp size={16} />
                              </IconButton>
                              <span>
                                {Math.max(
                                  0,
                                  feed.findIndex(
                                    (v) => v.id === displayVideo.id,
                                  ),
                                ) + 1}
                                <i> / {feed.length}</i>
                              </span>
                              <IconButton
                                label="下一个视频"
                                onClick={() => nextVideo()}
                              >
                                <ArrowDown size={16} />
                              </IconButton>
                            </div>
                          </TemplateBlock>
                        </TemplateFeedBlocks>
                      ) : (
                        <Empty
                          title={
                            channel === "关注"
                              ? "关注喜欢的创作者"
                              : selectedNavigation
                                ? moreLoading || hasMore
                                  ? "正在查找频道内容"
                                  : "暂时没有匹配的视频"
                                : "这个分类还在准备中"
                          }
                          text={
                            channel === "关注"
                              ? "点击视频右侧头像上的 +，在这里看 TA 的作品"
                              : selectedNavigation
                                ? moreLoading || hasMore
                                  ? "正在读取更多视频…"
                                  : "试试其他菜单，或返回全部推荐"
                                : "试试其他分类，更多内容即将到来"
                          }
                        >
                          <button
                            className="primary"
                            onClick={() => {
                              setChannel("推荐");
                              setCategory("全部推荐");
                              setSelectedNavigationId("");
                            }}
                          >
                            浏览推荐
                          </button>
                        </Empty>
                      )}
                    </TemplateBlock>
                    <TemplateBlock id="catalog">
                      <div className="template-home-catalog">
                        {feed.length ? (
                          cardGrid(feed)
                        ) : (
                          <Empty title="暂无匹配内容" text="试试其他菜单" />
                        )}
                      </div>
                    </TemplateBlock>
                  </TemplatePage>
                )}

                {nav.page === "discover" && (
                  <TemplatePage
                    id="discover"
                    className="scroll-page discover-page"
                  >
                    <TemplateBlock id="header">
                      <header className="simple-header">
                        <div>
                          <span className="eyebrow">EXPLORE</span>
                          <h1>{pageConfig("discover").title}</h1>
                        </div>
                        <IconButton label="搜索" onClick={() => openSearch()}>
                          <Search />
                        </IconButton>
                      </header>
                    </TemplateBlock>
                    <TemplateBlock id="filters">
                      <div className="category-chips">
                        {["全部推荐", ...categories].map((c) => (
                          <button
                            className={discoverCategory === c ? "selected" : ""}
                            key={c}
                            onClick={() => setDiscoverCategory(c)}
                          >
                            {c === "全部推荐" ? "全部" : c}
                          </button>
                        ))}
                      </div>
                    </TemplateBlock>
                    <TemplateBlock id="featured">
                      <button
                        className="featured-card"
                        onClick={() => {
                          setDiscoverCategory(
                            IS_DEMO ? "旅行" : menu.types[0] || "全部推荐",
                          );
                        }}
                      >
                        <img src="/media/lake.jpg" alt="" />
                        <div>
                          <span>WEEKLY SELECTION</span>
                          <h2>
                            {IS_DEMO ? "去看更大的世界" : "发现你的下一部好剧"}
                          </h2>
                          <p>
                            让心动，成为出发的理由 <ChevronRight size={14} />
                          </p>
                        </div>
                        <span className="featured-count">本周精选</span>
                      </button>
                    </TemplateBlock>
                    <TemplateBlock id="heading">
                      <div className="section-title">
                        <h2>
                          {discoverCategory === "全部推荐"
                            ? "为你发现"
                            : discoverCategory}
                        </h2>
                        <span>每一帧都值得</span>
                      </div>
                    </TemplateBlock>
                    <TemplateBlock id="videos">
                      {filterVideos(
                        items.filter((v) => !v.private),
                        discoverCategory,
                      ).length ? (
                        cardGrid(
                          filterVideos(
                            items.filter((v) => !v.private),
                            discoverCategory,
                          ),
                        )
                      ) : (
                        <Empty
                          title="精彩正在路上"
                          text="这个频道暂无演示内容，试试影视、旅行或生活"
                        />
                      )}
                    </TemplateBlock>
                  </TemplatePage>
                )}

                {nav.page === "messages" && (
                  <TemplatePage
                    id="messages"
                    className="scroll-page messages-page"
                  >
                    <TemplateBlock id="header">
                      <header className="simple-header">
                        <h1>{pageConfig("messages").title}</h1>
                        <IconButton
                          label="通知设置"
                          onClick={() => navigate("settings")}
                        >
                          <Settings size={23} />
                        </IconButton>
                      </header>
                    </TemplateBlock>
                    <TemplateBlock id="shortcuts">
                      <div className="message-shortcuts">
                        {[
                          { icon: Heart, title: "赞和收藏", n: 12 },
                          { icon: MessageCircle, title: "评论", n: 3 },
                          { icon: UserRoundPlus, title: "新增粉丝", n: 8 },
                          { icon: Bell, title: "系统通知", n: 0 },
                        ].map(({ icon: Icon, title, n }) => (
                          <button
                            key={title}
                            onClick={() =>
                              showInfo(
                                title,
                                `这里将展示${title}动态。当前为前端演示，接入消息 API 后可查看实时通知。`,
                              )
                            }
                          >
                            <span>
                              <Icon size={24} />
                              {IS_DEMO && n > 0 && <i>{n}</i>}
                            </span>
                            <small>{title}</small>
                          </button>
                        ))}
                      </div>
                    </TemplateBlock>
                    <TemplateBlock id="heading">
                      <div className="section-title">
                        <h2>最近聊天</h2>
                        <span>本地演示</span>
                      </div>
                    </TemplateBlock>
                    <TemplateBlock id="conversations">
                      <div className="conversation-list">
                        {!IS_DEMO && (
                          <Empty
                            title="暂无会话"
                            text="现有项目未暴露用户消息接口，消息中心已预留接入位置"
                          />
                        )}
                        {(IS_DEMO ? conversations : []).map((c) => (
                          <button
                            key={c.name}
                            onClick={() => {
                              setChatName(c.name);
                              navigate("messages", "chat");
                            }}
                          >
                            <img src={c.image} alt="" />
                            <div>
                              <h3>
                                {c.name}
                                <time>{c.time}</time>
                              </h3>
                              <p>
                                {chats[c.name]?.at(-1) || c.text}
                                {IS_DEMO &&
                                  c.unread &&
                                  !chats[c.name]?.length && <b>1</b>}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </TemplateBlock>
                    <TemplateBlock id="hint">
                      <p className="page-hint">
                        <LockKeyhole size={12} /> 聊天仅在当前演示中展示
                      </p>
                    </TemplateBlock>
                  </TemplatePage>
                )}

                {nav.page === "profile" && (
                  <TemplatePage
                    id="profile"
                    className="scroll-page profile-page"
                  >
                    <TemplateBlock id="cover">
                      <div className="profile-cover">
                        <img src="/media/city.jpg" alt="" />
                        <div className="profile-toolbar">
                          <IconButton
                            label="我的收藏"
                            onClick={() => navigate("collection")}
                          >
                            <Bookmark size={22} />
                          </IconButton>
                          <IconButton
                            label="设置"
                            onClick={() => navigate("settings")}
                          >
                            <Menu size={25} />
                          </IconButton>
                        </div>
                      </div>
                    </TemplateBlock>
                    <TemplateBlock id="info">
                      <div className="profile-info">
                        <div className="profile-avatar">
                          <img src="/media/night-portrait.jpg" alt="我的头像" />
                        </div>
                        <h1>
                          {store.profile.name}
                          <span>
                            {member.user?.memberType ||
                              (IS_DEMO ? "创作者" : "游客")}
                          </span>
                        </h1>
                        <p className="profile-id">
                          {member.loading
                            ? "正在恢复登录…"
                            : member.user
                              ? `樱桃号 · ${member.user.id}`
                              : "本机游客 · 尚未登录"}
                        </p>
                        {member.error && (
                          <p className="auth-error">{member.error}</p>
                        )}
                        <p className="profile-bio">{store.profile.bio}</p>
                        <div className="profile-stats">
                          {[
                            {
                              n: items
                                .filter((v) => v.own && !v.private)
                                .reduce((n, v) => n + v.likes, 0),
                              label: "获赞",
                            },
                            { n: store.followed.length, label: "关注" },
                            { n: IS_DEMO ? 24000 : 0, label: "粉丝" },
                          ].map((s) => (
                            <div key={s.label}>
                              <strong>{formatCount(s.n)}</strong>
                              <span>{s.label}</span>
                            </div>
                          ))}
                        </div>
                        <div className="profile-buttons">
                          <button
                            onClick={() => {
                              if (!IS_DEMO && !member.user) {
                                navigate("profile", "auth");
                                return;
                              }
                              setEditName(store.profile.name);
                              setEditBio(store.profile.bio);
                              navigate("profile", "edit");
                            }}
                          >
                            {!IS_DEMO && !member.user
                              ? "登录 / 注册"
                              : "编辑资料"}
                          </button>
                          <button onClick={() => navigate("collection")}>
                            <Layers size={16} /> 收藏与历史
                          </button>
                        </div>
                      </div>
                    </TemplateBlock>
                    <TemplateBlock id="tabs">
                      <div className="underline-tabs">
                        {["作品", "私密", "喜欢"].map((t) => (
                          <button
                            className={profileTab === t ? "selected" : ""}
                            key={t}
                            onClick={() => setProfileTab(t)}
                          >
                            {t}
                            <small>
                              {t === "作品"
                                ? items.filter((v) => v.own && !v.private)
                                    .length
                                : t === "喜欢"
                                  ? store.liked.length
                                  : items.filter((v) => v.own && v.private)
                                      .length}
                            </small>
                          </button>
                        ))}
                      </div>
                    </TemplateBlock>
                    <TemplateBlock id="videos">
                      {profileTab === "私密" ? (
                        items.some((v) => v.own && v.private) ? (
                          <div className="private-list">
                            {items
                              .filter((v) => v.own && v.private)
                              .map((v) => (
                                <article key={v.id}>
                                  <video
                                    src={v.src}
                                    controls
                                    playsInline
                                    preload="metadata"
                                  />
                                  <h3>{v.title}</h3>
                                  <small>仅自己可见 · 本地演示</small>
                                </article>
                              ))}
                          </div>
                        ) : (
                          <Empty
                            title="这里是你的私密空间"
                            text="发布时选择「仅自己可见」，就可以保存珍藏的瞬间"
                          />
                        )
                      ) : (profileTab === "喜欢"
                          ? items.filter((v) => store.liked.includes(v.id))
                          : items.filter((v) => v.own && !v.private)
                        ).length ? (
                        cardGrid(
                          profileTab === "喜欢"
                            ? items.filter((v) => store.liked.includes(v.id))
                            : items.filter((v) => v.own && !v.private),
                        )
                      ) : (
                        <Empty
                          title="留住你喜欢的瞬间"
                          text="点赞的视频会出现在这里"
                        />
                      )}
                    </TemplateBlock>
                  </TemplatePage>
                )}

                {nav.page === "search" && (
                  <TemplatePage id="search" className="scroll-page search-page">
                    <TemplateBlock id="header">
                      <form
                        className="search-header"
                        onSubmit={(e) => {
                          e.preventDefault();
                          search(query);
                        }}
                      >
                        <div className="search-field">
                          <Search size={18} />
                          <input
                            autoFocus
                            aria-label="搜索视频、用户、合集"
                            placeholder="搜索视频、用户、合集"
                            value={query}
                            onChange={(e) => {
                              setQuery(e.target.value);
                              if (!e.target.value) setSearchTerm("");
                            }}
                          />
                          <IconButton
                            label="清空搜索"
                            onClick={() => {
                              setQuery("");
                              setSearchTerm("");
                            }}
                          >
                            <X size={16} />
                          </IconButton>
                        </div>
                        <button
                          className="text-button"
                          type="button"
                          onClick={back}
                        >
                          取消
                        </button>
                        <button type="submit" className="hidden-input">
                          搜索
                        </button>
                      </form>
                    </TemplateBlock>
                    <TemplateBlock id="results">
                      {searchTerm ? (
                        <>
                          <div className="section-title">
                            <h2>搜索结果</h2>
                            <span>“{searchTerm}”</span>
                          </div>
                          {filterVideos(
                            items.filter((v) => !v.private),
                            "全部推荐",
                            searchTerm,
                          ).length ? (
                            cardGrid(
                              filterVideos(
                                items.filter((v) => !v.private),
                                "全部推荐",
                                searchTerm,
                              ),
                            )
                          ) : (
                            <Empty
                              title="没有找到相关内容"
                              text="换个关键词试试，例如「城市」「旅行」或「生活」"
                            />
                          )}
                        </>
                      ) : (
                        <>
                          <div className="section-title">
                            <h2>最近搜索</h2>
                            <IconButton
                              label="清空搜索历史"
                              onClick={() =>
                                update((s) => ({ ...s, searches: [] }))
                              }
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </div>
                          <div className="search-history">
                            {store.searches.length ? (
                              store.searches.map((t) => (
                                <button key={t} onClick={() => search(t)}>
                                  {t}
                                </button>
                              ))
                            ) : (
                              <span>搜索喜欢的视频和合集</span>
                            )}
                          </div>
                          <div className="section-title hot-title">
                            <h2>{IS_DEMO ? "热门搜索" : "内容推荐"}</h2>
                            <span>
                              {IS_DEMO ? "大家都在看" : "来自已加载内容"}
                            </span>
                          </div>
                          <ol className="hot-list">
                            {suggestedSearches.map((t, i) => (
                              <li key={t}>
                                <button onClick={() => search(t)}>
                                  <b>{i + 1}</b>
                                  <span>{t}</span>
                                  {IS_DEMO && i < 3 && <i>热</i>}
                                  {IS_DEMO && (
                                    <small>
                                      {(256.3 - i * 29.7).toFixed(1)}万
                                    </small>
                                  )}
                                </button>
                              </li>
                            ))}
                          </ol>
                        </>
                      )}
                    </TemplateBlock>
                  </TemplatePage>
                )}

                {nav.page === "collection" && (
                  <TemplatePage
                    id="collection"
                    className="scroll-page library-page"
                  >
                    <TemplateBlock id="header">
                      <Header
                        title={pageConfig("collection").title}
                        onBack={back}
                      />
                    </TemplateBlock>
                    <TemplateBlock id="tabs">
                      <div className="underline-tabs">
                        {["视频", "合集", "历史"].map((t) => (
                          <button
                            className={libraryTab === t ? "selected" : ""}
                            key={t}
                            onClick={() => setLibraryTab(t)}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </TemplateBlock>
                    <TemplateBlock id="content">
                      {libraryTab === "合集" ? (
                        <>
                          {store.collections.length ? (
                            <div className="library-list">
                              {store.collections.map((name) => {
                                const v = items.find(
                                  (v) => v.collection === name,
                                );
                                return (
                                  <button
                                    key={name}
                                    onClick={() => {
                                      if (v) openVideo(v);
                                      else notify("暂无可播放内容");
                                    }}
                                  >
                                    <img
                                      src={v?.cover || "/media/city.jpg"}
                                      alt=""
                                    />
                                    <div>
                                      <h2>{name}</h2>
                                      <p>
                                        {
                                          items.filter(
                                            (v) => v.collection === name,
                                          ).length
                                        }{" "}
                                        个演示作品
                                      </p>
                                      <span>收藏的故事，随时继续</span>
                                      <div className="library-progress">
                                        <i />
                                      </div>
                                      <small>
                                        继续观看 <ChevronRight size={12} />
                                      </small>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          ) : (
                            <Empty
                              title="收藏一个好故事"
                              text="在视频选集面板点击「收藏合集」，就能在这里继续观看"
                            >
                              <button
                                className="primary"
                                onClick={() => navigate("home", "episodes")}
                              >
                                发现合集
                              </button>
                            </Empty>
                          )}
                        </>
                      ) : (
                        (() => {
                          const list =
                            libraryTab === "历史"
                              ? store.history
                                  .map((id) => items.find((v) => v.id === id))
                                  .filter((v): v is VideoItem => !!v)
                              : items.filter((v) => store.saved.includes(v.id));
                          return list.length ? (
                            <>
                              {libraryTab === "历史" && (
                                <div className="library-action">
                                  <button
                                    className="text-button"
                                    onClick={() =>
                                      update((s) => ({ ...s, history: [] }))
                                    }
                                  >
                                    清空观看历史
                                  </button>
                                </div>
                              )}
                              {cardGrid(list)}
                            </>
                          ) : (
                            <Empty
                              title={
                                libraryTab === "历史"
                                  ? "还没有观看记录"
                                  : "还没有收藏视频"
                              }
                              text="喜欢的内容，随时回来继续看"
                            />
                          );
                        })()
                      )}
                    </TemplateBlock>
                  </TemplatePage>
                )}

                {nav.page === "settings" && (
                  <TemplatePage
                    id="settings"
                    className="scroll-page settings-page"
                  >
                    <TemplateBlock id="header">
                      <Header
                        title={pageConfig("settings").title}
                        onBack={back}
                      />
                    </TemplateBlock>
                    <TemplateBlock id="account">
                      <div className="settings-group">
                        <button
                          className="settings-row"
                          onClick={() =>
                            member.user
                              ? showInfo(
                                  "账号与安全",
                                  `当前账号：${member.user.identifier}。会话有效期为7天，可通过退出登录撤销当前会话。`,
                                )
                              : navigate("settings", "auth")
                          }
                        >
                          <ShieldCheck size={20} />
                          <span>账号与安全</span>
                          <ChevronRight size={18} />
                        </button>
                        <button
                          className="settings-row"
                          onClick={() => setPrivacyOpen(!privacyOpen)}
                        >
                          <LockKeyhole size={20} />
                          <span>隐私设置</span>
                          <ChevronDown size={18} />
                        </button>
                        {privacyOpen && (
                          <p className="settings-explanation">
                            演示数据只保存在当前设备。相机和麦克风仅在你主动开启拍摄时申请权限。
                          </p>
                        )}
                      </div>
                    </TemplateBlock>
                    <TemplateBlock id="preferences">
                      <div className="settings-group">
                        {[
                          {
                            key: "notifications",
                            icon: Bell,
                            title: "消息通知",
                            sub: "本机偏好，待接入推送",
                          },
                          {
                            key: "autoplay",
                            icon: Play,
                            title: "自动播放",
                            sub: "进入可播放的视频时自动播放",
                          },
                          {
                            key: "muted",
                            icon: Volume2,
                            title: "默认静音",
                            sub: "每次打开默认静音，可在播放器内开启声音",
                          },
                          {
                            key: "comments",
                            icon: MessageCircle,
                            title: "允许评论",
                            sub: "我的发布偏好",
                          },
                        ].map(({ key, icon: Icon, title, sub }) => (
                          <label className="settings-row" key={key}>
                            <Icon size={20} />
                            <span>
                              {title}
                              <small>{sub}</small>
                            </span>
                            <input
                              className="toggle-input"
                              type="checkbox"
                              aria-label={title}
                              checked={
                                store.settings[
                                  key as keyof SavedState["settings"]
                                ]
                              }
                              onChange={(e) =>
                                update((s) => ({
                                  ...s,
                                  settings: {
                                    ...s.settings,
                                    [key]: e.target.checked,
                                  },
                                }))
                              }
                            />
                            <i className="toggle" />
                          </label>
                        ))}
                      </div>
                    </TemplateBlock>
                    <TemplateBlock id="about">
                      <div className="settings-group">
                        <button
                          className="settings-row"
                          onClick={() =>
                            showInfo(
                              "帮助与反馈",
                              "上下滑动切换视频；左上角菜单选择分类；点击合集选集。带「可播放」标记的内容可播放，其他内容是概念封面预览。正式反馈入口需要接入客服服务。",
                            )
                          }
                        >
                          <HelpCircle size={20} />
                          <span>帮助与反馈</span>
                          <ChevronRight size={18} />
                        </button>
                        <button
                          className="settings-row"
                          onClick={() =>
                            showInfo(
                              "关于樱桃视频",
                              "樱桃视频 CHERRY · v0.1.0。React + TypeScript H5，预留 Capacitor iOS / Android 封装。当前为前端演示版，所有本地互动均不会提交到服务器。",
                            )
                          }
                        >
                          <Info size={20} />
                          <span>关于我们</span>
                          <small>v0.1.0</small>
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    </TemplateBlock>
                    <TemplateBlock id="reset">
                      <button
                        className="reset-local"
                        onClick={() => {
                          localStorage.removeItem(STORAGE_KEY);
                          setStore(readState());
                          notify("已清除本机互动和偏好");
                        }}
                      >
                        <Trash2 size={16} /> 清除本机演示数据
                      </button>
                    </TemplateBlock>
                    <TemplateBlock id="logout">
                      <button
                        className="logout"
                        disabled={signingOut}
                        onClick={async () => {
                          if (!member.user) {
                            navigate("settings", "auth");
                            return;
                          }
                          setSigningOut(true);
                          try {
                            await member.signOut();
                            update((s) => ({
                              ...s,
                              profile: {
                                name: "游客",
                                bio: "登录后完善你的个人资料",
                              },
                            }));
                            notify("已退出登录");
                          } catch (error) {
                            notify(
                              error instanceof Error
                                ? error.message
                                : "退出失败",
                            );
                          } finally {
                            setSigningOut(false);
                          }
                        }}
                      >
                        <LogOut size={17} />{" "}
                        {signingOut
                          ? "正在退出…"
                          : member.user
                            ? "退出登录"
                            : "登录 / 注册"}
                      </button>
                    </TemplateBlock>
                  </TemplatePage>
                )}

                {nav.page === "create" && (
                  <TemplatePage id="create" className="camera-template">
                    <TemplateBlock id="camera">
                      <CameraPage
                        title={pageConfig("create").title}
                        onClose={back}
                        onFile={selectFile}
                        notify={notify}
                      />
                    </TemplateBlock>
                  </TemplatePage>
                )}
                {nav.page === "publish" && (
                  <TemplatePage
                    id="publish"
                    className="scroll-page publish-page"
                  >
                    <TemplateBlock id="header">
                      <Header
                        title={pageConfig("publish").title}
                        onBack={back}
                      />
                    </TemplateBlock>
                    <TemplateBlock id="note">
                      <div className="demo-note">
                        <Info size={15} /> 本地演示 · 视频不会上传到服务器
                      </div>
                    </TemplateBlock>
                    <TemplateBlock id="composer">
                      <div className="publish-composer">
                        <label className="upload-cover">
                          {upload ? (
                            <video
                              src={upload.url}
                              muted
                              playsInline
                              preload="metadata"
                            />
                          ) : (
                            <ImagePlus />
                          )}
                          <span>{upload ? "更换视频" : "选择视频"}</span>
                          <input
                            type="file"
                            accept="video/*"
                            onChange={(e) => {
                              if (e.target.files?.[0])
                                selectFile(e.target.files[0]);
                            }}
                          />
                        </label>
                        <div>
                          <textarea
                            aria-label="作品描述"
                            maxLength={500}
                            placeholder="添加作品描述，让更多人看到你的精彩…"
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                          />
                          <div className="publish-tags">
                            <button
                              onClick={() =>
                                setCaption((s) => `${s} #${publishCategory} `)
                              }
                            >
                              <Hash size={13} /> 添加话题
                            </button>
                            <button
                              onClick={() => setCaption((s) => `${s} @好友 `)}
                            >
                              <AtSign size={13} /> 提及好友
                            </button>
                          </div>
                          <small>{caption.length}/500</small>
                        </div>
                      </div>
                    </TemplateBlock>
                    <TemplateBlock id="options">
                      <div className="publish-options">
                        <label className="settings-row">
                          <Film size={19} />
                          <span>作品分类</span>
                          <select
                            aria-label="作品分类"
                            value={publishCategory}
                            onChange={(e) => setPublishCategory(e.target.value)}
                          >
                            {categories.map((c) => (
                              <option key={c}>{c}</option>
                            ))}
                          </select>
                        </label>
                        <label className="settings-row">
                          <Layers size={19} />
                          <span>加入合集</span>
                          <select
                            aria-label="加入合集"
                            value={publishCollection}
                            onChange={(e) =>
                              setPublishCollection(e.target.value)
                            }
                          >
                            {[
                              "不加入合集",
                              "城市故事",
                              "旅行日记",
                              "生活切片",
                            ].map((c) => (
                              <option key={c}>{c}</option>
                            ))}
                          </select>
                        </label>
                        <label className="settings-row">
                          <LockKeyhole size={19} />
                          <span>谁可以看</span>
                          <select
                            aria-label="谁可以看"
                            value={visibility}
                            onChange={(e) => setVisibility(e.target.value)}
                          >
                            <option>公开</option>
                            <option>仅自己可见</option>
                          </select>
                        </label>
                        <label className="settings-row">
                          <MessageCircle size={19} />
                          <span>允许评论</span>
                          <input
                            className="toggle-input"
                            type="checkbox"
                            aria-label="本作品允许评论"
                            checked={allowComments}
                            onChange={(e) => setAllowComments(e.target.checked)}
                          />
                          <i className="toggle" />
                        </label>
                      </div>
                    </TemplateBlock>
                    <TemplateBlock id="hint">
                      <p className="publish-hint">
                        发布偏好供界面演示使用，正式权限由服务端执行。刷新页面后需要重新选择视频文件。
                      </p>
                    </TemplateBlock>
                    <TemplateBlock id="footer">
                      <div className="publish-footer">
                        <button
                          onClick={() => {
                            update((s) => ({ ...s, draft: caption }));
                            notify("文字草稿已保存到本机");
                          }}
                        >
                          存草稿
                        </button>
                        <button className="primary" onClick={publish}>
                          发布到本地演示
                        </button>
                      </div>
                    </TemplateBlock>
                  </TemplatePage>
                )}
                {!IS_DEMO &&
                  hasMore &&
                  ["discover", "search"].includes(nav.page) && (
                    <div className="catalog-load-more">
                      <button
                        className="primary"
                        onClick={loadMore}
                        disabled={moreLoading}
                      >
                        {moreLoading
                          ? "正在加载…"
                          : `加载更多 · 已载入 ${items.filter((v) => v.remote).length}/${total}`}
                      </button>
                    </div>
                  )}
              </main>
              {hasNav && navFooter}
            </div>

            {nav.overlay === "categories" && (
              <Modal drawer title="视频分类" onClose={back}>
                <p className="drawer-subtitle">选择你感兴趣的内容</p>
                <div className="search-field drawer-search">
                  <Search size={17} />
                  <input
                    aria-label="搜索分类"
                    placeholder="搜索分类"
                    value={categoryQuery}
                    onChange={(e) => setCategoryQuery(e.target.value)}
                  />
                </div>
                <button
                  className={`all-categories ${category === "全部推荐" ? "selected" : ""}`}
                  onClick={() => chooseCategory("全部推荐")}
                >
                  <Grid2X2 size={24} />
                  <span>全部推荐</span>
                  <ChevronRight size={19} />
                </button>
                <div className="section-title">
                  <h3>
                    {IS_DEMO
                      ? "热门分类"
                      : customNavigation?.displayTitle ||
                        customNavigation?.name ||
                        "自定义导航"}
                  </h3>
                  <span>{drawerItems.length} 个频道</span>
                </div>
                <div className="category-grid">
                  {drawerItems.map((item, i) => {
                    const Icon = categoryIcons[i] || Film;
                    return (
                      item.name.includes(categoryQuery.trim()) && (
                        <button
                          className={
                            (
                              IS_DEMO
                                ? category === item.name
                                : selectedNavigationId === item.id
                            )
                              ? "selected"
                              : ""
                          }
                          key={item.id}
                          onClick={() => chooseNavigation(item)}
                        >
                          {item.icon ? (
                            <span aria-hidden="true">{item.icon}</span>
                          ) : (
                            <Icon size={21} />
                          )}
                          <span>{item.name}</span>
                        </button>
                      )
                    );
                  })}
                </div>
                {navigationLoading && (
                  <p className="page-hint">正在读取导航…</p>
                )}
                {!navigationLoading &&
                  !navigationError &&
                  !drawerItems.some((item) =>
                    item.name.includes(categoryQuery.trim()),
                  ) && (
                    <p className="page-hint">
                      {categoryQuery.trim()
                        ? "没有找到这个分类"
                        : "暂无启用的菜单"}
                    </p>
                  )}
                {navigationError && (
                  <div className="page-hint">
                    <p>{navigationError}</p>
                    <button className="primary" onClick={retryNavigation}>
                      重新读取导航
                    </button>
                  </div>
                )}
                <p className="drawer-hint">点击分类，切换视频频道</p>
                <div className="drawer-brand">
                  <span className="brand-icon">
                    <Play size={13} fill="currentColor" />
                  </span>
                  樱桃视频 CHERRY
                </div>
              </Modal>
            )}

            {nav.overlay === "episodes" && (
              <Modal title={displayVideo.collection} onClose={back}>
                <div className="collection-meta">
                  {displayVideo.remote
                    ? `${collectionEpisodes.length} 个可播放分集 · 共${displayVideo.episodeCount}集`
                    : `${collectionEpisodes.length} 个演示作品 · ${displayVideo.src ? "可播放示例" : "概念封面预览"}`}
                  <span>持续更新中</span>
                </div>
                {detailLoading && (
                  <p className="page-hint">正在读取分集列表…</p>
                )}
                {detailError && (
                  <button
                    className="primary"
                    onClick={() => void loadDetail(displayVideo.id)}
                  >
                    分集读取失败，重试
                  </button>
                )}
                <div className="episode-tabs">
                  <div className="underline-tabs">
                    {["选集", "简介"].map((t) => (
                      <button
                        key={t}
                        className={episodeTab === t ? "selected" : ""}
                        onClick={() => setEpisodeTab(t)}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <button
                    className="text-button"
                    onClick={() => setReverseEpisodes((v) => !v)}
                  >
                    <SlidersHorizontal size={14} />{" "}
                    {reverseEpisodes ? "倒序" : "正序"}
                  </button>
                </div>
                {episodeTab === "简介" ? (
                  <div className="collection-description">
                    <h3>关于这份合集</h3>
                    <p>
                      {displayVideo.remote
                        ? displayVideo.description
                        : "用镜头收藏生活，把值得记住的瞬间连成一个好故事。"}
                    </p>
                    <p>
                      {displayVideo.remote
                        ? displayVideo.description
                        : "当前采用概念封面，正式内容接入后支持连续播放。"}
                    </p>
                  </div>
                ) : (
                  <div className="episode-list">
                    {(reverseEpisodes
                      ? [...collectionEpisodes].reverse()
                      : collectionEpisodes
                    ).map((ep) => (
                      <button
                        key={ep.id || ep.number}
                        className={
                          (displayVideo.remote
                            ? displayVideo.episode
                            : episodeNumber) === ep.number
                            ? "selected"
                            : ""
                        }
                        onClick={() => {
                          setEpisodeNumber(ep.number);
                          if (displayVideo.remote) {
                            setItems((s) =>
                              s.map((v) =>
                                v.id === displayVideo.id
                                  ? { ...v, episode: ep.number, src: ep.src }
                                  : v,
                              ),
                            );
                            back();
                            return;
                          }
                          const selected = items.find(
                            (v) =>
                              v.episode === ep.number &&
                              v.collection === displayVideo.collection,
                          );
                          if (selected) setCurrentId(selected.id);
                          back();
                          if (!selected?.src)
                            notify(`已选择第${ep.number}集 · 当前为封面预览`);
                        }}
                      >
                        <img src={ep.cover} alt="" />
                        <div>
                          <h3>
                            {ep.title.startsWith("第")
                              ? ep.title
                              : `第${ep.number}集 · ${ep.title}`}
                          </h3>
                          <p>
                            {ep.duration}
                            {(displayVideo.remote
                              ? displayVideo.episode
                              : episodeNumber) === ep.number && (
                              <span>
                                <span className="equalizer">▂▆▃</span> 当前选集
                              </span>
                            )}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                <div className="sheet-footer">
                  <button
                    className="primary full-width"
                    onClick={() =>
                      toggle("collections", displayVideo.collection)
                    }
                  >
                    <Star
                      size={18}
                      fill={
                        store.collections.includes(displayVideo.collection)
                          ? "currentColor"
                          : "none"
                      }
                    />
                    {store.collections.includes(displayVideo.collection)
                      ? "已收藏合集"
                      : "收藏合集"}
                  </button>
                </div>
              </Modal>
            )}

            {nav.overlay === "comments" && (
              <Modal
                title={`${displayVideo.remote ? "本机评论" : "全部评论"} ${displayVideo.comments + Math.max(0, comments.length - baseComments.length)}`}
                onClose={back}
              >
                {displayVideo.remote && (
                  <p className="demo-note">
                    当前未发现用户评论接口，评论仅保存在本机
                  </p>
                )}
                <div className="comment-list">
                  {comments.map((c, i) => (
                    <article key={c.id}>
                      <div className="comment-avatar">
                        {i === 0 ? (
                          <img src="/media/night-portrait.jpg" alt="" />
                        ) : (
                          c.user.slice(0, 1)
                        )}
                      </div>
                      <div className="comment-body">
                        <h3>{c.user}</h3>
                        <p>{c.text}</p>
                        <div className="comment-meta">
                          <span>{c.time}</span>
                          <button
                            onClick={() => {
                              setCommentReply(c.user);
                              document
                                .getElementById("comment-input")
                                ?.focus({ preventScroll: true });
                            }}
                          >
                            回复
                          </button>
                        </div>
                        {c.reply && (
                          <div className="comment-reply">{c.reply}</div>
                        )}
                      </div>
                      <button
                        className={`comment-like ${c.liked ? "liked" : ""}`}
                        aria-label={`赞同${c.user}的评论`}
                        aria-pressed={!!c.liked}
                        onClick={() =>
                          update((s) => ({
                            ...s,
                            comments: {
                              ...s.comments,
                              [displayVideo.id]: (
                                s.comments[displayVideo.id] || baseComments
                              ).map((x) =>
                                x.id === c.id ? { ...x, liked: !x.liked } : x,
                              ),
                            },
                          }))
                        }
                      >
                        <Heart
                          size={17}
                          fill={c.liked ? "currentColor" : "none"}
                        />
                        <span>{c.likes + Number(!!c.liked)}</span>
                      </button>
                    </article>
                  ))}
                </div>
                <form className="comment-form" onSubmit={addComment}>
                  {commentReply && (
                    <button
                      className="reply-label"
                      type="button"
                      onClick={() => setCommentReply("")}
                    >
                      回复 @{commentReply}
                      <X size={12} />
                    </button>
                  )}
                  <input
                    id="comment-input"
                    aria-label="评论内容"
                    placeholder={
                      commentReply
                        ? `回复 @${commentReply}`
                        : "说点什么，分享你的感受…"
                    }
                    value={commentText}
                    maxLength={300}
                    onChange={(e) => setCommentText(e.target.value)}
                  />
                  <button
                    className="primary"
                    type="submit"
                    disabled={!commentText.trim()}
                  >
                    发送
                  </button>
                </form>
              </Modal>
            )}

            {nav.overlay === "share" && (
              <Modal title="分享这份精彩" onClose={back}>
                <div className="share-preview">
                  <img src={displayVideo.cover} alt="" />
                  <div>
                    <h3>{displayVideo.title}</h3>
                    <p>@{displayVideo.creator}</p>
                  </div>
                </div>
                <div className="share-actions">
                  <button
                    onClick={() =>
                      void shareVideo(displayVideo.title, displayVideo.id)
                        .then(notify)
                        .catch((e) => {
                          if (e?.name !== "AbortError")
                            notify(
                              e instanceof Error ? e.message : "分享已取消",
                            );
                        })
                    }
                  >
                    <ExternalLink />
                    <span>系统分享 / 复制链接</span>
                  </button>
                  <button
                    onClick={() => {
                      toggle("saved", displayVideo.id);
                      notify(
                        store.saved.includes(displayVideo.id)
                          ? "已取消收藏"
                          : "已收藏到本机",
                      );
                    }}
                  >
                    <Bookmark />
                    <span>
                      {store.saved.includes(displayVideo.id)
                        ? "取消收藏"
                        : "收藏视频"}
                    </span>
                  </button>
                </div>
                <p className="page-hint">演示链接仅在可访问的 H5 地址下有效</p>
              </Modal>
            )}

            {nav.overlay === "auth" && (
              <AuthDialog
                onClose={back}
                signIn={member.signIn}
                onSuccess={() => {
                  back();
                  notify("登录成功");
                }}
              />
            )}

            {nav.overlay === "edit" && (
              <Modal title="编辑资料" onClose={back}>
                <form
                  className="edit-profile"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!editName.trim()) {
                      notify("昵称不能为空");
                      return;
                    }
                    setProfileSaving(true);
                    try {
                      if (!IS_DEMO)
                        await member.updateProfile(
                          editName.trim(),
                          editBio.trim(),
                        );
                      else
                        update((s) => ({
                          ...s,
                          profile: {
                            name: editName.trim(),
                            bio: editBio.trim(),
                          },
                        }));
                      back();
                      notify(IS_DEMO ? "资料已保存到本机" : "资料已保存");
                    } catch (error) {
                      notify(
                        error instanceof Error ? error.message : "保存失败",
                      );
                    } finally {
                      setProfileSaving(false);
                    }
                  }}
                >
                  <label>
                    昵称
                    <input
                      aria-label="昵称"
                      maxLength={20}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                  </label>
                  <label>
                    个人简介
                    <textarea
                      aria-label="个人简介"
                      maxLength={80}
                      value={editBio}
                      onChange={(e) => setEditBio(e.target.value)}
                    />
                  </label>
                  <button
                    className="primary full-width"
                    type="submit"
                    disabled={profileSaving}
                  >
                    {profileSaving ? "正在保存…" : "保存资料"}
                  </button>
                </form>
              </Modal>
            )}

            {nav.overlay === "chat" && (
              <Modal title={chatName} onClose={back}>
                <p className="demo-note">本地会话演示，消息不会发送给其他人</p>
                <div className="chat-list">
                  <p className="chat-received">
                    {conversations.find((c) => c.name === chatName)?.text}
                  </p>
                  {(chats[chatName] || []).map((text, i) => (
                    <p className="chat-sent" key={i}>
                      {text}
                    </p>
                  ))}
                </div>
                <form
                  className="comment-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!chatText.trim()) return;
                    setChats((s) => ({
                      ...s,
                      [chatName]: [...(s[chatName] || []), chatText.trim()],
                    }));
                    setChatText("");
                  }}
                >
                  <input
                    aria-label="消息内容"
                    placeholder="发送本地演示消息…"
                    value={chatText}
                    onChange={(e) => setChatText(e.target.value)}
                    maxLength={300}
                  />
                  <button
                    type="submit"
                    className="primary"
                    disabled={!chatText.trim()}
                  >
                    <Send size={18} />
                  </button>
                </form>
              </Modal>
            )}
            {nav.overlay === "notice" && (
              <Modal title={noticeTitle} onClose={back}>
                <div className="notice-content">
                  <Info size={28} />
                  <p>{noticeText}</p>
                  <button className="primary full-width" onClick={back}>
                    知道了
                  </button>
                </div>
              </Modal>
            )}
            {toast && (
              <div className="toast" role="status">
                {toast}
              </div>
            )}
          </div>
          <aside className="desktop-notes">
            <div className="device-note">
              <span className="online-dot" /> 移动端交互预览
            </div>
            <h2>
              好故事，
              <br />
              值得<span>停留。</span>
            </h2>
            <p>
              上滑发现下一份精彩
              <br />
              点击左上角，探索你的热爱
            </p>
            <div className="preview-guide">
              <span>01</span>
              <div>
                沉浸式视频<small>触摸上下滑动切换内容</small>
              </div>
            </div>
            <div className="preview-guide">
              <span>02</span>
              <div>
                分类随心看<small>后台分类同步 · 左侧抽屉</small>
              </div>
            </div>
            <div className="preview-guide">
              <span>03</span>
              <div>
                收藏好故事<small>合集、评论与创作</small>
              </div>
            </div>
            <div className="demo-disclaimer">
              前端演示版
              <br />
              真实视频库 · HLS 播放
              <br />
              互动数据保存在当前设备
            </div>
          </aside>
        </div>
      </TemplateRuntimeProvider>
    </TemplateProvider>
  );
}
